import React, { ElementType } from 'react';

import { OpenInNewTabIcon } from '@modernloop/shared/icons';
import { Link as MuiLink, LinkProps as MuiLinkProps, Typography, TypographyProps } from '@mui/material';
import { LocationDescriptor } from 'history';

import { BaseProps } from 'src/components/types';

import useIsInExtension from 'src/recruiting-portal/pages/extension/utils/useIsInExtension';

export type Props = BaseProps &
  Omit<MuiLinkProps, 'variant' | 'color'> & {
    color?: TypographyProps['color'];
    variant?: TypographyProps['variant'];
    fontWeight?: TypographyProps['fontWeight'];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component?: ElementType<any>;
    to?: LocationDescriptor;
  };

const Link = ({
  dataTestId,
  variant,
  color,
  children,
  fontWeight,
  style,
  target,
  rel,
  to,
  href,
  ...props
}: Props): JSX.Element => {
  const isInExtension = useIsInExtension();
  // If target is explicitly passed from outside then use it otherwise default open new tab when in extension
  const resolvedTarget = isInExtension ? target || '_blank' : target;
  // A link we force open in a new tab must not give the new page a `window.opener` reference, so
  // default rel to noopener/noreferrer whenever we resolved target to _blank ourselves.
  const resolvedRel = isInExtension && resolvedTarget === '_blank' ? rel || 'noopener noreferrer' : rel;
  // Only show the new-tab icon when this actually IS a link, i.e. it has a destination. `Link` is
  // also used as a styled action button (onClick only, no `to`/`href`); those never navigate
  // anywhere, so resolving target to _blank for them must not draw an icon promising a new tab.
  const hasDestination = to !== undefined || href !== undefined;
  const showNewTabIcon = isInExtension && resolvedTarget === '_blank' && hasDestination;

  return (
    <MuiLink
      color={color === 'inherit' ? 'inherit' : undefined}
      {...props}
      {...{ to, href }}
      style={{ cursor: 'pointer', ...style }}
      data-testid={dataTestId}
      target={resolvedTarget}
      rel={resolvedRel}
    >
      <Typography variant={variant} fontWeight={fontWeight} color={color} style={{ display: 'inline' }}>
        {children}
      </Typography>
      {/* Narrow condition, on purpose: we only know a link opens a new tab when we ourselves
          resolved it to _blank inside the extension AND it has a destination. Callers who pass
          target="_blank" explicitly outside the extension keep today's appearance; showing the
          icon there would be an app-wide visual change out of scope here. */}
      {showNewTabIcon && (
        <OpenInNewTabIcon
          data-testid="link-new-tab-icon"
          fontSize="inherit"
          aria-hidden
          style={{ verticalAlign: 'middle', marginLeft: 2 }}
        />
      )}
    </MuiLink>
  );
};

export default Link;
